<template>
  <div class="management-shell">
    <a class="management-skip-link" href="#management-main" @click.prevent="focusMain">跳到主要内容</a>
    <aside class="management-sidebar">
      <div class="management-brand">
        <img :src="logoImage" alt="" />
        <span>开源物联可视化</span>
        <button ref="navigationToggle" class="management-nav-toggle" type="button" :aria-expanded="String(mobileNavOpen)" aria-controls="management-navigation" :aria-label="mobileNavOpen ? '收起管理菜单' : '展开管理菜单'" @click="toggleMobileNav"><span>{{ activeSection ? activeSection.title : '菜单' }}</span><i :class="mobileNavOpen ? 'el-icon-close' : 'el-icon-menu'" aria-hidden="true"></i></button>
      </div>
      <nav ref="navigation" id="management-navigation" class="management-nav" :class="{ 'is-mobile-open': mobileNavOpen }" aria-label="可视化管理导航" @keydown.esc.stop.prevent="closeMobileNav(true)">
        <button v-for="section in topLevelSections" :key="section.key" type="button" :aria-current="activeSection && activeSection.key === section.key ? 'page' : null" @click="handleSelect(section.key)">
          <i :class="section.icon" aria-hidden="true"></i><span>{{ section.title }}</span>
        </button>
        <div v-if="resourceSections.length" class="management-nav-group">
          <button type="button" :class="{ 'is-active': activeSection && ['images', 'components', 'templates'].includes(activeSection.key) }" :aria-expanded="String(resourceExpanded)" aria-controls="management-resource-submenu" @click="resourceExpanded = !resourceExpanded">
            <i class="el-icon-folder-opened" aria-hidden="true"></i><span>资源中心</span>
            <i :class="resourceExpanded ? 'el-icon-arrow-up' : 'el-icon-arrow-down'" class="management-nav-arrow" aria-hidden="true"></i>
          </button>
          <div v-show="resourceExpanded" id="management-resource-submenu" class="management-subnav">
            <button v-for="section in resourceSections" :key="section.key" type="button" :aria-current="activeSection && activeSection.key === section.key ? 'page' : null" @click="handleSelect(section.key)">
              <i :class="section.icon" aria-hidden="true"></i><span>{{ section.title }}</span>
            </button>
          </div>
        </div>
      </nav>
    </aside>
    <div class="management-workspace">
      <header class="management-topbar">
        <nav aria-label="当前位置" class="management-breadcrumb">
          <ol><li>可视化</li><li v-if="activeSection && ['images', 'components', 'templates'].includes(activeSection.key)">资源中心</li><li aria-current="page">{{ activeSection ? activeSection.title : '暂无可用菜单' }}</li></ol>
        </nav>
        <div class="management-account">
          <span :title="userInfo && userInfo.name">{{ userInfo && userInfo.name || '当前账号' }}</span>
          <el-button type="text" @click="handleLogout">退出账号</el-button>
        </div>
      </header>
      <main ref="main" id="management-main" tabindex="-1" class="management-main">
        <list v-if="activeSection && ['1', 'scada2d'].includes(activeSection.key)" :key="activeSection.key" :project-type="activeSection.key === 'scada2d' ? 'scada2d' : 'big_screen'"></list>
        <control-groups v-else-if="activeSection && activeSection.key === 'groups'"></control-groups>
        <maps v-else-if="activeSection && activeSection.key === '2'"></maps>
        <image-resources v-else-if="activeSection && activeSection.key === 'images'"></image-resources>
        <template-resources v-else-if="activeSection && activeSection.key === 'components'" key="component-resources" resource-mode></template-resources>
        <template-resources v-else-if="activeSection && activeSection.key === 'templates'" key="template-resources" ref="templates" @browse-projects="handleSelect('1')"></template-resources>
        <p v-else class="management-no-access" role="status">当前账号没有可用的管理菜单，请联系管理员授权。</p>
      </main>
    </div>
  </div>
</template>

<script>
import list from "./list/index";
import maps from "./list/map";
import ImageResources from '@/components/ImageResources/browser.vue'
import TemplateResources from '@/components/Templates/browser.vue'
import ControlGroups from '@/components/ControlGroups/browser.vue'
import { loginout } from "@/api/auth";

import store from "@/store"

export default {
  name: "index",
  components: {
    list,
    maps,
    ImageResources,
    TemplateResources,
    ControlGroups,
  },
  data() {
    return {
      activeName: ['templates', 'components', 'images', 'groups', 'scada2d'].includes(this.$route.query.section) ? this.$route.query.section : '1',
      resourceExpanded: false,
      mobileNavOpen: false,
      userInfo: store.getters['user/userInfo'],
      logoImage: require("@/assets/logo.png"),
    };
  },
  computed: {
    managementSections () {
      return [
        { key: '1', title: '大屏', icon: 'el-icon-monitor', permission: 'kydz/View/Data:query' },
        { key: 'scada2d', title: '2D组态', icon: 'el-icon-cpu', permission: 'kydz/View/Data:query' },
        { key: 'groups', title: '设备群组', icon: 'el-icon-connection', resourcePermission: 'visualization:query' },
        { key: '2', title: '地图管理', icon: 'el-icon-location-outline', permission: 'system/Region:add' },
        { key: 'images', title: '图片', icon: 'el-icon-picture-outline', resourcePermission: 'visualizationImage:query' },
        { key: 'components', title: '组件', icon: 'el-icon-set-up', resourcePermission: 'visualizationComponent:query' },
        { key: 'templates', title: '模板', icon: 'el-icon-collection', resourcePermission: 'visualizationTemplate:query' }
      ].filter(section => section.resourcePermission ? store.getters['auth/hasResourcePermission'](section.resourcePermission) : this.hash(section.permission))
    },
    topLevelSections () { return this.managementSections.filter(section => !['images', 'components', 'templates'].includes(section.key)) },
    resourceSections () { return this.managementSections.filter(section => ['images', 'components', 'templates'].includes(section.key)) },
    activeSection () {
      return this.managementSections.find(section => section.key === this.activeName) || this.managementSections[0] || null
    }
  },
  methods: {
    toggleMobileNav () {
      if (this.mobileNavOpen) { this.closeMobileNav(true); return }
      this.mobileNavOpen = true
      if (this.activeSection && ['images', 'components', 'templates'].includes(this.activeSection.key)) this.resourceExpanded = true
      this.$nextTick(() => { const current = this.$refs.navigation && this.$refs.navigation.querySelector('[aria-current="page"]'); if (current) current.focus() })
    },
    closeMobileNav (returnFocus = false) {
      const wasOpen = this.mobileNavOpen
      this.mobileNavOpen = false
      if (wasOpen && returnFocus) this.$nextTick(() => { const toggle = this.$refs.navigationToggle; if (toggle && toggle.offsetParent) toggle.focus({ preventScroll: true }) })
    },
    focusMain () { this.$refs.main.focus() },
    hash(code) {
      return store.getters["auth/isAuth"](code);
    },
    handleSelect(key) {
      if (!this.managementSections.some(section => section.key === key)) return
      this.activeName = key;
      this.closeMobileNav(true)
      if (key === 'templates') this.$nextTick(() => { if (this.$refs.templates) this.$refs.templates.openProjectTemplates() });
    },
    async handleLogout() {
      try {
        loginout().then((res) => {
          if (res.status == 200) {
            localStorage.removeItem("x-access-token");
            store.dispatch('auth/clearPermissions')
            store.dispatch('user/logout')
            this.$router.push("/login"); // 跳转登录页
          }
        });
      } catch (error) {
        this.$message.error("退出失败");
      }
    },
  },
};
</script>

<style lang="scss">
@import "~@/styles/list.scss";
</style>
